/**
 * Domain types — shared between the React app and the Worker.
 * These are the canonical shapes. API response types derive from them.
 */

/**
 * The six CEFR levels, in order. Mirrored on the server at
 * functions/api/lib/levels.ts. Kept duplicated to keep src/ and functions/
 * decoupled — the values are fixed by the CEFR specification.
 */
export const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;
export type CefrLevel = (typeof CEFR_LEVELS)[number];

export type AppStatus = 'active' | 'coming_soon';

export type InstalledApp = {
  id: string;
  /** The language this app teaches. Users only see apps they are learning. */
  language: string;
  display_name: string;
  description: string | null;
  icon: string; // Lucide component name
  status: AppStatus;
  sort_order: number;
};

export type AppStats = {
  streak: number;
  practiced_today: boolean;
  learning: number;
  known: number;
  archived: number;
};

export type AppWithStats = InstalledApp & {
  stats: AppStats | null;
};

/**
 * A stretch of days the streak is not judged on. Both ends inclusive; a null
 * `to_date` is a pause with no return date yet.
 */
export type StreakPause = {
  id: number;
  from_date: string;
  to_date: string | null;
  reason: string | null;
  created_at?: string;
};

export type OverallStreak = {
  current: number;
  longest: number;
  freezes_available: number;
  last_streak_date: string | null;
  week_activity: boolean[];
  /** Parallel to `week_activity`: days declared off rather than days missed. */
  week_paused: boolean[];
  /** The pause covering today, or null. */
  pause: StreakPause | null;
  week_start: string;
  /** The user's effective date, so the client never has to guess it. */
  today: string;
};

export type OverallStats = {
  memorized: number;
  total_cards: number;
  total_sessions: number;
  overall_accuracy: number;
};

// --- Card ---

export type Translations = Record<string, string[]>;
export type ExampleTranslations = Record<string, string>;

export type NounsPayload = {
  article: 'der' | 'die' | 'das';
  plural: string;
};

/**
 * Verb payload.
 *
 * The conjugation fields are nullable because the source lists disagree: A2 and
 * B1 print the forms next to the infinitive, A1 gives the bare infinitive. They
 * are stored even though this app only tests translation — they were free while
 * the PDFs were open, and the card back shows them for nothing.
 */
export type VerbPayload = {
  /** Separable prefix: anfangen → er fängt an. */
  separable: boolean;
  /** Listed with (sich): (sich) freuen. */
  reflexive: boolean;
  /** 3rd person singular present, e.g. "fängt an". */
  present_3sg: string | null;
  praeteritum: string | null;
  /** Perfect with its auxiliary, e.g. "hat angefangen". */
  perfekt: string | null;
  auxiliary: 'haben' | 'sein' | null;
};

/**
 * The eight fields of a Konjugation card, in the order they are asked.
 *
 * Six persons then the two principal parts. The keys are stable and short
 * because they are also the keys in `attempts.detail`, where they accumulate
 * into the per-field history the card back reads — renaming one silently
 * orphans every row already logged under the old name.
 */
export const KONJUGATION_FIELDS = [
  'ich',
  'du',
  'er',
  'wir',
  'ihr',
  'sie',
  'praeteritum',
  'perfekt',
] as const;
export type KonjugationField = (typeof KONJUGATION_FIELDS)[number];

/** What each field is labelled on the card. */
export const KONJUGATION_LABELS: Record<KonjugationField, string> = {
  ich: 'ich',
  du: 'du',
  er: 'er/sie/es',
  wir: 'wir',
  ihr: 'ihr',
  sie: 'sie/Sie',
  praeteritum: 'Präteritum',
  perfekt: 'Perfekt',
};

/**
 * Konjugation payload — the answer key for one verb.
 *
 * Derived at seed time by `scripts/conjugate.ts` and stored, rather than
 * computed in the browser. Three reasons, in order of how much they matter:
 * a stored table can be audited and corrected as data (including through the
 * in-app card editor, like every other field); the app ships no morphology
 * engine; and a verb whose forms cannot be derived is caught by the seed rather
 * than rendering as a broken card.
 *
 * `praeteritum` and `perfekt` are asked in the third person singular, which is
 * how the Wortlisten print them — "arbeitete", "hat gearbeitet". They stay
 * nullable because a verb may legitimately lack one: `fahren` takes both
 * auxiliaries and the source printed neither. A card simply asks one field
 * fewer.
 */
export type KonjugationPayload = {
  /** Separable prefix — the card shows it, since it changes where the verb goes. */
  separable: boolean;
  /** Präsens, indexed to match the first six of `KONJUGATION_FIELDS`. */
  praesens: [string, string, string, string, string, string];
  /** Präteritum, er/sie/es. */
  praeteritum: string | null;
  /** Perfekt with its auxiliary, er/sie/es: "hat gearbeitet", "ist gefahren". */
  perfekt: string | null;
  auxiliary: 'haben' | 'sein' | null;
  /** Whether the Präsens came from the rule or from a hardcoded table. */
  praesens_source: 'rule' | 'table';
  /**
   * The verb's only possible subject is `es` — the weather verbs.
   *
   * Such a card asks the third person and the two principal parts only. The
   * other five present forms are well-formed German that no context produces,
   * and asking for them spends a session on words that must not be said.
   */
  impersonal?: boolean;
};

/**
 * Per-field result of one Konjugation attempt, stored as `attempts.detail`.
 *
 * `tries` is how many submissions that field took: 0 means right first time,
 * which is the only thing that counts toward the card's boolean. Anything
 * higher means the user got there, but by retrying.
 *
 * `revealed` means the user asked for the answer instead of producing it. That
 * is a different fact from missing it twice and is kept separate rather than
 * folded into `tries`: a form you keep getting wrong and a form you have never
 * been able to recall are different problems, and only one of them is visible
 * in a retry count. Both count as a miss for the card and for the history.
 */
export type KonjugationDetail = {
  fields: { key: KonjugationField; tries: number; revealed?: boolean }[];
};

/** Lifetime misses per field for one card, newest-first aggregate. */
export type KonjugationFieldStats = {
  card_id: number;
  /** Sessions this card has been answered in. */
  attempts: number;
  /** Field key → number of attempts where it was not right first time. */
  misses: Partial<Record<KonjugationField, number>>;
};

/**
 * Adjective / adverb payload.
 *
 * German mostly does not separate the two — `schnell` is both "fast" and
 * "quickly" with no change of form — so they share one deck and `usage` records
 * which reading applies.
 */
export type AdjectivePayload = {
  usage: 'adjective' | 'adverb' | 'both';
  comparative: string | null;
  superlative: string | null;
};

/** ISO 3166-2 subdivision codes for the 16 German Bundesländer. */
export const BUNDESLAND_CODES = [
  'BW',
  'BY',
  'BE',
  'BB',
  'HB',
  'HH',
  'HE',
  'MV',
  'NI',
  'NW',
  'RP',
  'SL',
  'SN',
  'ST',
  'SH',
  'TH',
] as const;
export type BundeslandCode = (typeof BUNDESLAND_CODES)[number];

/** Display names for the 16 Bundesländer. Single source of truth — imported
 *  by both the einbuergerungstest AppHome chip and the Settings picker. */
export const BUNDESLAND_NAMES: Record<BundeslandCode, string> = {
  BW: 'Baden-Württemberg',
  BY: 'Bayern',
  BE: 'Berlin',
  BB: 'Brandenburg',
  HB: 'Bremen',
  HH: 'Hamburg',
  HE: 'Hessen',
  MV: 'Mecklenburg-Vorpommern',
  NI: 'Niedersachsen',
  NW: 'Nordrhein-Westfalen',
  RP: 'Rheinland-Pfalz',
  SL: 'Saarland',
  SN: 'Sachsen',
  ST: 'Sachsen-Anhalt',
  SH: 'Schleswig-Holstein',
  TH: 'Thüringen',
};

export const EINBUERGERUNGS_TOPICS = [
  'grundrechte',
  'geschichte',
  'geographie',
  'demokratie',
  'staat',
  'other',
] as const;
export type EinbuergerungsTopic = (typeof EINBUERGERUNGS_TOPICS)[number];

export type OptionLetter = 'A' | 'B' | 'C' | 'D';

export type EinbuergerungsOption = {
  letter: OptionLetter;
  de: string;
  en: string;
};

export type EinbuergerungsPayload = {
  scope: 'general' | 'state';
  state_code: BundeslandCode | null;
  topic: EinbuergerungsTopic;
  has_image: boolean;
  question_en: string;
  options: EinbuergerungsOption[];
  correct_letter: OptionLetter;
  tip_en: string | null;
};

// --- einbuergerungstest-vokabeln ---

export type VokabelPos = 'noun' | 'verb' | 'adjective';

/** One segment of a compound noun or separable-prefix verb. */
export type VokabelPart = {
  /** The segment as it appears inside the headword (may carry a linking element). */
  surface: string;
  /** The standalone dictionary form of that segment. */
  lemma: string;
  en: string;
};

/**
 * How many BAMF questions a word unlocks. 1 = five or more, 2 = two to four,
 * 3 = exactly one. This is the axis the app organises study around: rarity is
 * not payoff, and a plain word appearing in fourteen questions earns more time
 * than an exotic one appearing in a single question.
 */
export type CoverageTier = 1 | 2 | 3;

/** A German sentence with its English translation. */
export type BilingualText = { de: string; en: string };

/**
 * Where in the source exam question the word actually appears.
 *
 * `'answer'` and `'question'` mean the word shows up in the Q&A pair the card
 * back displays. `'option'` means it only appears in a distractor — a sentence
 * the exam marks *false* — so the card must show it separately from the
 * correct answer rather than letting the two run together.
 */
export type ExampleSource = 'question' | 'answer' | 'option';

export type VokabelnPayload = {
  pos: VokabelPos;
  /** der/die/das for nouns, null otherwise. Drives the gender tint on the card. */
  article: 'der' | 'die' | 'das' | null;
  plural: string | null;
  /**
   * Curated pool of wrong English glosses. The card shows two of these, sampled
   * per card, so the option set varies between passes and the word has to be
   * read rather than the card recognised by shape.
   */
  distractors: string[];
  /** Compound / separable-prefix split, or null when the word does not decompose. */
  parts: VokabelPart[] | null;
  /** BAMF ids this word appears in. Derived at seed time from the catalog. */
  question_ids: string[];
  coverage_tier: CoverageTier;
  /** Where the word appears in the source question. Null for legacy rows. */
  example_source: ExampleSource | null;
  /** The source exam question. Shown on the card back with its answer. */
  example_question: BilingualText | null;
  /** That question's correct answer — the other half of the flashcard. */
  example_answer: BilingualText | null;
};

export type SimulationPreset = 'bamf' | 'hard';

/** Stored in `app_settings.config` for the einbuergerungstest app. */
export type EinbuergerungsConfig = {
  bundesland: BundeslandCode;
  simulation_preset: SimulationPreset;
};

export type Card<TPayload = unknown> = {
  id: number;
  app_id: string;
  /** External identifier for catalog-sourced apps (e.g. BAMF question number). */
  external_id?: string | null;
  de: string;
  translations: Translations;
  example: string | null;
  example_translations: ExampleTranslations;
  level: string | null;
  difficulty: 1 | 2 | 3;
  false_friend: boolean;
  warning: string | null;
  payload: TPayload;
  streak: number;
  last_attempt_correct: boolean | null;
  memorized: boolean;
  memorized_date: string | null;
  archived: boolean;
  /** Generic user-facing bookmark flag (einbuergerungstest surfaces it in v1). */
  bookmarked?: boolean;
  /** Flagged after three misses in a row; cleared by two correct in a row. */
  struggling?: boolean;
};

// --- Session lifecycle ---

/**
 * `mistakes` is the cards currently flagged as struggling — three misses in a
 * row, not yet answered right twice. A sibling of the other three rather than a
 * filter on them: it has its own pool, its own ordering (worst first) and its
 * own reason to exist.
 */
export type SessionMode = 'practice' | 'review' | 'archive' | 'mistakes' | 'lesson' | 'topic';

export type SessionStartResponse = {
  session_id: number;
  mode: SessionMode;
  cards: Card<NounsPayload>[];
};

export type SessionAttempt = {
  card_id: number;
  correct: boolean;
  time_ms: number;
  /**
   * What the user answered, in the app's own vocabulary. `null` is
   * "I don't know". Persisted since migration 0006 — it is what lets the
   * mistakes list say *how* a card is being missed rather than only how often.
   */
  user_answer?: string | null;
  /**
   * App-specific breakdown, stored as JSON in `attempts.detail`. Only
   * Konjugation sends one (`KonjugationDetail`) — see `AnswerEvent.detail`.
   */
  detail?: unknown;
};

export type StreakSummary = {
  current_streak: number;
  longest_streak: number;
  last_streak_date: string | null;
  freezes_available: number;
};

/**
 * One card the user got wrong, kept for the end-of-session review list.
 *
 * Client-side only: the practice shell already holds every card and every
 * answer, so the misses ride to the results screen in router state alongside
 * the score. Nothing is fetched back, which is why refreshing the results
 * page loses the list — the same thing that already happens to the score.
 */
export type SessionMiss<TPayload = unknown> = {
  card: Card<TPayload>;
  /**
   * What the user answered, in the app's own vocabulary: free text (nouns),
   * an article (derdiedas), an English gloss (vokabeln), an option letter
   * (einbuergerungstest), or `null` for "I don't know".
   */
  user_answer: string | null;
};

export type SessionFinishResponse = {
  session_id: number;
  app_id: string;
  cards_total: number;
  cards_correct: number;
  accuracy: number;
  streak_eligible: boolean;
  streak: StreakSummary;
  apps_done_today: { id: string; name: string }[];
  apps_remaining_today: { id: string; name: string }[];
};

// --- Per-app stats ---

export type StreakDistribution = {
  s0: number;
  s1: number;
  s2: number;
  s3: number;
  s4plus: number;
};

export type ActivityHeatmapData = {
  weeks: number;
  start_date: string;
  end_date: string;
  days: { date: string; sessions: number }[];
};

export type LevelProgressRow = {
  level: CefrLevel;
  total: number;
  /** non-memorized AND non-archived — the Practice pool */
  learning: number;
  /** memorized AND non-archived — the Review pool */
  known: number;
  /** archived — the Archive review pool */
  archived: number;
  /**
   * known + archived. The "level-complete" rule: a level is 100% when
   * `learning === 0` (every card is either known or archived).
   */
  memorized: number;
};

export type TierProgressRow = {
  tier: CoverageTier;
  total: number;
  /** non-memorized AND non-archived — the Practice pool */
  learning: number;
  /** memorized AND non-archived — the Review pool */
  known: number;
  /** archived — the Archive review pool */
  archived: number;
  /** known + archived */
  memorized: number;
  /** Distinct exam questions the words in this tier appear in. */
  questions_covered: number;
};

export type TierProgress = {
  tiers: TierProgressRow[];
  /**
   * Distinct questions covered by the whole deck.
   *
   * Kept separate because per-tier counts OVERLAP — one question can contain
   * words from several tiers — so summing them across chips would claim more
   * questions than the exam contains. Only a single tier's figure or this
   * deck-wide one may be shown.
   */
  questions_covered_total: number;
};

export type SimulationStatsSummary = {
  total_taken: number;
  total_passed: number;
  pass_rate: number;
  best_correct: number;
  best_total: number;
  average_correct: number;
  /** Chronological order — left-to-right sparkline. */
  trend: {
    sim_id: number;
    correct: number;
    total: number;
    passed: boolean;
    finished_at: string;
  }[];
};

export type AppStatsResponse = {
  /** Cards currently flagged as "keeps getting me". Drives the Mistakes mode. */
  struggling_count: number;
  metrics: {
    current_streak: number;
    longest_streak: number;
    overall_accuracy: number;
    total_sessions: number;
  };
  streak_distribution: StreakDistribution;
  activity_heatmap: ActivityHeatmapData;
  /** Per-CEFR-level rollup. Levels with `total === 0` are still present. */
  level_progress: LevelProgressRow[];
  /**
   * Per-coverage-tier rollup. Null for every app whose cards carry no tier —
   * which is how the app home knows to show CEFR chips (or none) instead.
   */
  tier_progress: TierProgress | null;
  /** Null when the app has no completed simulations (e.g. nouns/derdiedas). */
  simulations: SimulationStatsSummary | null;
};

// --- Settings ---

export type UserSettingsRow = {
  /**
   * What things are EXPLAINED in. Distinct from `target_langs`, which is what
   * you are learning — the same code space named from two directions. One
   * person's target is another person's aux: a Brazilian learning German
   * explained in English and Portuguese, and a German learning Portuguese
   * explained in German and English, share this table.
   */
  primary_aux_lang: string;
  secondary_aux_lang: string;
  /** JSON array of the languages this user is learning, e.g. `["de","pt-BR"]`. */
  target_langs: string;
  timezone: string;
  grace_hours: number;
};

export type UserStreakSettings = {
  freezes_per_month: number;
  freezes_auto_spend: number;
  freezes_available: number;
};

export type StreakSettingRow = {
  app_id: string;
  included_in_overall: boolean;
  sessions_required_per_day: number;
};

export type AppSettingRow = {
  app_id: string;
  session_length: number;
  archive_threshold_days: number;
  review_session_length: number;
  archive_review_session_length: number;
  unarchive_failure_threshold: number;
};

export type SettingsResponse = {
  user: UserSettingsRow;
  user_streak: UserStreakSettings;
  streak: StreakSettingRow[];
  apps: AppSettingRow[];
};

// --- Import ---

export type ImportRow = {
  de: string;
  article?: 'der' | 'die' | 'das';
  plural?: string;
  en: string[];
  pt: string[];
  example?: string | null;
  examplePt?: string | null;
  level?: string | null;
  difficulty: 1 | 2 | 3;
  falseFriend: boolean;
  warning?: string;
};

export type ImportPreviewResponse = {
  new: ImportRow[];
  duplicates: { row: ImportRow; existing_app_ids: string[] }[];
  warnings: { row: ImportRow; issues: string[] }[];
  target_app_ids: string[];
};

export type ImportCommitResponse = {
  ok: true;
  imported: number;
};

// --- Struggling cards ("what keeps getting me") ---

/** One distinct wrong answer for a card, with how often it was given. */
export type WrongAnswerTally = {
  answer: string;
  times: number;
};

export type StrugglingCard = {
  id: number;
  external_id: string | null;
  de: string;
  translations: Translations;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any;
  streak: number;
  /** Consecutive misses. Higher = currently going worse. */
  wrong_streak: number;
  memorized: boolean;
  bookmarked: boolean;
  updated_at: string;
  /**
   * Distinct wrong answers, most-repeated first, capped at three. Empty for
   * cards whose misses all predate migration 0006 — those attempts recorded
   * only right/wrong, never the answer.
   */
  wrong_answers: WrongAnswerTally[];
  counts: { correct: number; incorrect: number };
};

export type StrugglingResponse = {
  app_id: string;
  cards: StrugglingCard[];
};
